import {SchoolIcon} from "../../components/icons/school";
import {AddButton, Button} from "@ciq/shared/components/buttons";
import {Slider} from "@ciq/shared/components/slider";
import Search from "@ciq/shared/components/search";
import {forwardRef, useEffect, useReducer, useRef, useState} from "react";
import {EntryModal, EntryInput, EntrySelector} from "@ciq/shared/components/modal";
import Api from "@ciq/shared/api/api";
import {ListView} from "@ciq/shared/components/list";
import {EmptyView} from "@ciq/shared/components/views";
import {SearchBox} from "@mapbox/search-js-react";
import {getAccessKey} from "@ciq/shared/hooks/map";
import {SemesterFormFields} from "@ciq/shared/pages/school/components/semester_form_fields";
import {ErrorToast} from "@ciq/shared/utils/toast";



const TIMEZONE_OPTIONS = [
  {label: "Eastern (America/New_York)", value: "America/New_York"},
  {label: "Central (America/Chicago)", value: "America/Chicago"},
  {label: "Mountain (America/Denver)", value: "America/Denver"},
  {label: "Pacific (America/Los_Angeles)", value: "America/Los_Angeles"},
  {label: "Alaska (America/Anchorage)", value: "America/Anchorage"},
  {label: "Hawaii (Pacific/Honolulu)", value: "Pacific/Honolulu"},
]

const DEFAULT_FORM_DATA = {
  name: "",
  address: "",
  city: "",
  state: "",
  country: "",
  zip: "",
  latitude: "",
  longitude: "",
  timezone: null,
  semester_name: null,
  semester_start_date: null,
  semester_end_date: null,
  partner_association: false,
}

const DEFAULT_STATE = {
  search: {
    isSearching: false,
    value: null,
    lowered: null
  },
  newModal: {
    show: false
  }
}

const SET_SEARCH = "SET_SEARCH"
const SHOW_NEW_MODAL = "SHOW_NEW_MODAL"

const SchoolListViewReducer = (state = DEFAULT_STATE, action) => {
  switch (action.type) {
    case SET_SEARCH:
      return {
        ...state,
        search: {
          isSearching: action.payload != null,
          value: action.payload,
          lowered: action.payload != null ? action.payload.toLowerCase() : null,
        }
      }

    case SHOW_NEW_MODAL:
      return {
        ...state,
        newModal: {
          show: action.payload
        }
      }

    default:
      throw new Error(`Unknown action type ${action.type}`)
  }
}

export function SchoolListView({school, schools, onSchoolAdded, onSchoolSelected}) {
  const [state, dispatch] = useReducer(SchoolListViewReducer, DEFAULT_STATE)
  const [listViewHeight, setListViewHeight] = useState("0")

  const viewRef = useRef(null)
  const headerRef = useRef(null)

  const viewBaseHeight = viewRef.current != null ? Math.floor(viewRef.current.getBoundingClientRect().height) : null
  const headerBaseHeight = headerRef.current != null ? Math.floor(headerRef.current.getBoundingClientRect().height) : null

  useEffect(() => {
    if (viewBaseHeight == null || headerBaseHeight == null) {
      return
    }

    const parentHeight = `calc(${viewBaseHeight}px - var(--content-inset) * 2)`
    const headerHeight = `calc(${headerBaseHeight}px + var(--content-inset))`

    const height = `calc(${parentHeight} - ${headerHeight})`
    if (height !== listViewHeight) {
      setListViewHeight(height)
    }
  }, [viewBaseHeight, headerBaseHeight, listViewHeight]);

  const setSearch = search => dispatch({type: SET_SEARCH, payload: search})

  const showNewModal = () => dispatch({type: SHOW_NEW_MODAL, payload: true})
  const hideNewModal = () => dispatch({type: SHOW_NEW_MODAL, payload: false})

  const handleOnSchoolAdded = (school) => {
    onSchoolAdded && onSchoolAdded(school, hideNewModal)
  }

  const filterSchool = (school) => {
    if (state.search.lowered == null) return true
    return school.id === state.search.lowered || school.name.toLowerCase().includes(state.search.lowered)
  }

  const filteredSchools = (schools || []).filter(filterSchool)
  const hasSchools = filteredSchools.length > 0

  return (
    <div ref={viewRef} className="school-list-view da-card">
      <HeaderView ref={headerRef} search={state.search.value} onSearch={setSearch} onNewSchoolClick={showNewModal}/>
      <ListView role="listbox" aria-label="Institutions">
        {!hasSchools && <EmptyView>No Institutions</EmptyView>}
        {hasSchools && filteredSchools.map(s =>
          <ListViewItem
            key={s.id}
            school={s}
            isSelected={school.id === s.id}
            onClick={onSchoolSelected}
          />
        )}
      </ListView>
      <NewSchoolModal
        show={state.newModal.show}
        onHide={hideNewModal}
        onNewSchoolClick={handleOnSchoolAdded}
      />
    </div>
  )

}

const HeaderView = forwardRef(({search, onSearch, onNewSchoolClick}, ref) => {
  const handleOnNewSchoolClick = () => {
    onNewSchoolClick && onNewSchoolClick();
  }

  return (
    <div ref={ref} className="school-list-view-header">
      <AddButton onClick={handleOnNewSchoolClick}>New Institution</AddButton>
      <Search search={search} onSearch={onSearch}/>
    </div>
  )
})

function ListViewItem({school, isSelected, onClick}) {
  const handleOnClick = () => {
    onClick && onClick(school)
  }

  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault()
      handleOnClick()
    }
  }

  return (
    <div
      className="school-list-view-item"
      role="option"
      tabIndex={0}
      aria-selected={isSelected}
      onClick={handleOnClick}
      onKeyDown={handleKeyDown}
    >
      <SchoolIcon school={school}/>
      <span>{school.name}</span>
    </div>
  )
}

// Export reducer and constants for testing
export {
  SchoolListViewReducer,
  DEFAULT_STATE,
  DEFAULT_FORM_DATA,
  TIMEZONE_OPTIONS,
  SET_SEARCH,
  SHOW_NEW_MODAL,
  fetchTimezone,
  findTimezoneOption,
}

/**
 * Fetch IANA timezone from backend for given coordinates.
 * @param {number} latitude
 * @param {number} longitude
 * @returns {Promise<string|null>} IANA timezone string or null on failure
 */
async function fetchTimezone(latitude, longitude, signal) {
  try {
    const response = await Api.get("schools/timezone", {params: {latitude, longitude}, signal})
    return response.data?.timezone || null
  } catch (e) {
    if (e?.name === "AbortError" || signal?.aborted) return null
    console.error("Failed to fetch timezone:", e)
    ErrorToast("Unable to detect timezone. Please select one manually.")
    return null
  }
}

/**
 * Find the matching timezone option for an IANA timezone string.
 * @param {string} tz - IANA timezone string
 * @returns {Object|null} Matching option from TIMEZONE_OPTIONS or a custom option
 */
function findTimezoneOption(tz) {
  if (!tz) return null
  const match = TIMEZONE_OPTIONS.find(o => o.value === tz)
  if (match) return match
  return {label: tz, value: tz}
}

function NewSchoolModal({show, onHide, onNewSchoolClick}) {
  const [data, setData] = useState(DEFAULT_FORM_DATA)
  const [searchBoxValue, setSearchBoxValue] = useState(undefined)
  const [showLocationWarning, setShowLocationWarning] = useState(false)
  const abortRef = useRef(null)
  useEffect(() => {
    if (!show) {
      if (abortRef.current) abortRef.current.abort()
      setData(DEFAULT_FORM_DATA)
      setSearchBoxValue(undefined)
      setShowLocationWarning(false)
    }
  }, [show])

  if (!show) return null

  const updateField = (field) => (value) => {
    setData(prev => ({ ...prev, [field]: value }))
  }

  const updateAddressField = (field) => (value) => {
    setData(prev => ({ ...prev, [field]: value }))
    if (searchBoxValue) setSearchBoxValue("")
  }

  const handleGeocoderRetrieve = async (result) => {
    const feature = result.features?.[0]
    if (!feature) return
    const {context, coordinates} = feature.properties

    const lat = coordinates?.latitude?.toString() || ""
    const lng = coordinates?.longitude?.toString() || ""

    setData(prev => ({
      ...prev,
      address: context.address?.name || "",
      city: context.place?.name || "",
      state: context.region?.name || "",
      country: context.country?.name || "",
      zip: context.postcode?.name || "",
      latitude: lat,
      longitude: lng,
    }))
    setShowLocationWarning(false)

    if (lat && lng) {
      if (abortRef.current) abortRef.current.abort()
      const controller = new AbortController()
      abortRef.current = controller
      const tz = await fetchTimezone(parseFloat(lat), parseFloat(lng), controller.signal)
      if (!controller.signal.aborted && tz) {
        setData(prev => ({...prev, timezone: findTimezoneOption(tz)}))
      }
    }
  }

  const isButtonDisabled = () => {
    const hasName = data.name?.trim()
    const hasSemester = data.semester_name && data.semester_start_date && data.semester_end_date
    const hasTimezone = !!data.timezone

    return !hasName || !hasSemester || !hasTimezone
  }

  const handleOnClick = () => {
    if (data.latitude === "" || data.longitude === "") {
      setShowLocationWarning(true)
      return
    }

    const payload = {
      name: data.name.trim(),
      address: data.address.trim(),
      city: data.city.trim(),
      state: data.state.trim(),
      country: data.country.trim(),
      zip: data.zip.trim(),
      latitude: parseFloat(data.latitude),
      longitude: parseFloat(data.longitude),
      timezone: data.timezone?.value || null,
    }

    payload.semester = {
      name: data.semester_name,
      start_date: data.semester_start_date,
      end_date: data.semester_end_date,
    }

    payload.partner_association = data.partner_association ? "apogee" : null

    onNewSchoolClick && onNewSchoolClick(payload)
  }

  return (
    <EntryModal
      show={show}
      title="Create Institution"
      onHide={onHide}
      cardClassName="create-institution-modal"
    >
      <EntryInput
        label="Name"
        value={data.name}
        autoComplete="organization"
        onChange={updateField("name")}
        autoFocus
      />

      <div className="create-institution-section-divider">Location</div>

      <SearchBox
        value={searchBoxValue}
        accessToken={getAccessKey()}
        onRetrieve={handleGeocoderRetrieve}
      />
      {showLocationWarning && (
        <div className="create-institution-location-warning">
          Please use the address search to set at least the city and country.
        </div>
      )}

      <EntryInput label="Address" value={data.address} onChange={updateAddressField("address")} />
      <div className="create-institution-address-row">
        <EntryInput label="City" value={data.city} onChange={updateAddressField("city")} />
        <EntryInput label="State" value={data.state} onChange={updateAddressField("state")} />
        <EntryInput label="Zip" value={data.zip} onChange={updateAddressField("zip")} />
      </div>
      <EntryInput label="Country" value={data.country} onChange={updateAddressField("country")} />

      <EntrySelector
        label="Timezone"
        placeholder="Select timezone..."
        value={data.timezone}
        options={TIMEZONE_OPTIONS}
        onChange={updateField("timezone")}
      />

      <div className="create-institution-section-divider">Initial Semester</div>
      <SemesterFormFields
        data={{
          name: data.semester_name,
          start_date: data.semester_start_date,
          end_date: data.semester_end_date,
        }}
        dateRowClassName="create-institution-date-row"
        onChange={(updates) => {
          setData(prev => ({
            ...prev,
            ...(updates.name !== undefined && {semester_name: updates.name}),
            ...(updates.start_date !== undefined && {semester_start_date: updates.start_date}),
            ...(updates.end_date !== undefined && {semester_end_date: updates.end_date}),
          }))
        }}
      />

      <div className="create-institution-footer">
        <div className="create-institution-footer-left">
          <span className="create-institution-toggle-label">Boldyn</span>
          <Slider
            checked={data.partner_association}
            onChecked={updateField("partner_association")}
          />
        </div>
        <div className="create-institution-footer-buttons">
          <Button variant="secondary" onClick={onHide}>Cancel</Button>
          <Button variant="primary" disabled={isButtonDisabled()} onClick={handleOnClick}>Create</Button>
        </div>
      </div>
    </EntryModal>
  )
}
